import React from "react";
import { FileText } from "lucide-react";
import { formatDisplayDate } from "../../../utils/Date";
import { getStatusBadge } from "./reportUtils";
import type { ArchivalRequest } from "./ArchivalRequest.types";

interface Props {
  modalOpen: boolean;
  selectedRequest: ArchivalRequest | null;
  userRole?: string;
  showRejectReason: boolean;
  rejectReason: string;
  setRejectReason: (value: string) => void;
  setShowRejectReason: (value: boolean) => void;
  setModalOpen: (value: boolean) => void;
  handleArchivalAction: (
    request: ArchivalRequest,
    action: "Approve" | "Reject",
  ) => void;
}

const ArchivalDetailsModal: React.FC<Props> = ({
  modalOpen,
  selectedRequest,
  userRole,
  showRejectReason,
  rejectReason,
  setRejectReason,
  setShowRejectReason,
  setModalOpen,
  handleArchivalAction,
}) => {
  if (!modalOpen || !selectedRequest) return null;

  const isActionDisabled =
    selectedRequest.status === "Rejected" ||
    selectedRequest.status === "Approved";

  const handleClose = () => {
    setModalOpen(false);
    setShowRejectReason(false);
    setRejectReason("");
  };

  const handleCancelReject = () => {
    setShowRejectReason(false);
    setRejectReason("");
  };

  const handleRejectSubmit = () => {
    handleArchivalAction(
      { ...selectedRequest, reason: rejectReason },
      "Reject",
    );

    setShowRejectReason(false);
    setRejectReason("");
  };

  const renderRejectSection = () => {
    if (!showRejectReason) {
      return (
        <>
          <button
            className={`bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold shadow transition ${
              isActionDisabled
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            onClick={() =>
              handleArchivalAction(selectedRequest, "Approve")
            }
            disabled={isActionDisabled}
          >
            Approve
          </button>

          <button
            className={`bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold shadow transition ${
              isActionDisabled
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            onClick={() => setShowRejectReason(true)}
            disabled={isActionDisabled}
          >
            Reject
          </button>
        </>
      );
    }

    return (
      <div className="w-full flex flex-col gap-3">
        <textarea
          className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500"
          rows={3}
          placeholder="Enter reason for rejection"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />

        <div className="flex gap-3 justify-end">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold transition"
            onClick={handleCancelReject}
          >
            Cancel
          </button>

          <button
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold shadow transition disabled:opacity-50"
            disabled={!rejectReason.trim()}
            onClick={handleRejectSubmit}
          >
            Submit Reject
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-8 animate-fade-in">
        <button
          className="absolute top-3 right-5 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
          onClick={handleClose}
          aria-label="Close"
        >
          &times;
        </button>

        <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-500" />
          Archival Request Details
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-gray-700 text-sm">
          <div>
            <span className="font-semibold">Request ID:</span>
            <div className="truncate">
              {selectedRequest.requestId || "—"}
            </div>
          </div>

          <div>
            <span className="font-semibold">CNR:</span>
            <div className="truncate">
              {selectedRequest.cnr || "—"}
            </div>
          </div>

          <div>
            <span className="font-semibold">User:</span>
            <div className="truncate">
              {selectedRequest.userId?.username ||
                `${selectedRequest.userId?.firstName || ""} ${
                  selectedRequest.userId?.lastName || ""
                }`.trim() ||
                "—"}
            </div>
          </div>

          <div>
            <span className="font-semibold">Email:</span>
            <div className="truncate">
              {selectedRequest.userId?.email || "—"}
            </div>
          </div>

          <div>
            <span className="font-semibold">Request Date:</span>
            <div>
              {formatDisplayDate(selectedRequest.createdAt)}
            </div>
          </div>

          <div>
            <span className="font-semibold">Decision Status:</span>
            <div>{getStatusBadge(selectedRequest.status)}</div>
          </div>

          <div>
            <span className="font-semibold">Controller:</span>
            <div className="truncate">
              {selectedRequest.controllerName || "—"}
            </div>
          </div>

          <div>
            <span className="font-semibold">Action Time:</span>
            <div>
              {selectedRequest.actionDateTime
                ? formatDisplayDate(selectedRequest.actionDateTime)
                : "—"}
            </div>
          </div>

          <div className="sm:col-span-2">
            <span className="font-semibold">
              Reason (if Rejected):
            </span>

            <div className="truncate">
              {selectedRequest.status === "Rejected"
                ? selectedRequest.reason || "—"
                : "—"}
            </div>
          </div>
        </div>

        {userRole === "Controller" && (
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
            {renderRejectSection()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchivalDetailsModal;